import { getBlockedCards, unblockCard } from 'APIs/blockedcards/blockedcards';
import { BlockedCard, BlockedCardsQueryParams } from 'APIs/blockedcards/blockedcards.types';
import { QueryParams } from 'APIs/types';
import FilterComponent from 'Components/Common/Filters/FilterComponent';
import { FilterOption } from 'Components/Common/Filters/types';
import HeaderTitle from 'Components/HeaderTitle/HeaderTitle';
import Message from 'Components/Message';
import Pagination from 'Components/Pagination/Pagination';
import Spinner from 'Components/Spinner/Spinner';
import { LpBox, LpDetails, P } from 'Constants/styles';
import { Content } from 'Constants/styles';
import { ReactComponent as DotsIcon } from 'assets/svg/dots.svg';
import { DateTime } from 'luxon';
import { FunctionComponent, MouseEvent as ReactMouseEvent, useCallback, useEffect, useRef, useState } from 'react';
import { ImBlocked, ImCheckmark } from 'react-icons/im';
import { MdClose, MdOutlineRemoveRedEye } from 'react-icons/md';
import { useSelector } from 'react-redux';
import { NavLink } from 'react-router-dom';
import getErrorMessage from 'utils/getErrorMessage';
import useClickOutside from 'utils/useClickOutside';

import { filterQueryParams, initFilterOptions } from './BlockedCardsFilter';
import InfoModal from './InfoModal';
import UnblockModal from './UnblockModal';
import { FilterBtn, FilterPopup, ItemList, PaginationRow, SearchBox, SpinnerWrapper } from './styles';
import { blockedCardEventTypeLabels } from './types';

const BlockedCards = () => {
  const { apiAprilBaseUri } = useSelector((state: ReduxStateType) => ({
    apiAprilBaseUri: state.apiAprilBaseUri,
  }));

  const CloseIcon = MdClose as FunctionComponent;
  const RemoveRedEyeIcon = MdOutlineRemoveRedEye as FunctionComponent;
  const BlockedIcon = ImBlocked as FunctionComponent;
  const CheckIcon = ImCheckmark as FunctionComponent;

  const [blockedCards, setBlockedCards] = useState<Array<BlockedCard>>([]);
  const [actionBlockedCardIdIndex, setActionBlockedCardIdIndex] = useState<string>('');
  const [actionBlockedCard, setActionBlockedCard] = useState<BlockedCard | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [errorMsg, setErrorMsg] = useState<string>('');
  const [successMsg, setSuccessMsg] = useState<string>('');

  const [searchText, setSearchText] = useState<string>('');
  const [searchTextInputValue, setSearchTextInputValue] = useState<string>('');
  const [filterPopupOpen, setFilterPopupOpen] = useState<boolean>(false);
  const [filterOptionValues, setFilterOptionValues] = useState<FilterOption[]>(initFilterOptions);
  const [filterCount, setFilterCount] = useState<number>(0);

  const [activePage, setActivePage] = useState<number>(1);
  const [pageStartIndex, setPageStartIndex] = useState<number>(1);
  const [pageCount, setPageCount] = useState<number>(1);

  const containerRef = useRef<HTMLDivElement>(null);
  const getFilterQueryParams = useCallback(filterQueryParams, []);

  const [showInfoModal, setShowInfoModal] = useState<boolean>(false);
  const [showUnblockModal, setShowUnblockModal] = useState<boolean>(false);
  const [isModalLoading, setIsModalLoading] = useState<boolean>(false);

  const fetchBlockedCards = useCallback(async () => {
    setErrorMsg('');
    setActionBlockedCardIdIndex('');
    setActionBlockedCard(null);
    if (!apiAprilBaseUri) {
      return;
    }

    try {
      setIsLoading(true);

      const qParams: QueryParams = getFilterQueryParams(searchText, filterOptionValues);

      const resp = await getBlockedCards(apiAprilBaseUri, {
        ...(qParams as BlockedCardsQueryParams),
        page: activePage,
        sort: '-createdAt',
      });
      setBlockedCards(resp.items);
      setPageCount(resp.totalPages);
    } catch (e) {
      setErrorMsg(getErrorMessage(e));
    } finally {
      setIsLoading(false);
    }
  }, [activePage, apiAprilBaseUri, filterOptionValues, getFilterQueryParams, searchText]);

  const unBlockCard = useCallback(
    async (cardBlockId: string) => {
      setErrorMsg('');
      if (!apiAprilBaseUri) {
        return;
      }
      const last4 =
        actionBlockedCard && actionBlockedCard.paymentMethod?.paymentMethodType === 'Card'
          ? actionBlockedCard.paymentMethod.cardLast4
          : '-';
      try {
        setIsModalLoading(true);
        await unblockCard(apiAprilBaseUri, cardBlockId);
        setSuccessMsg(`You have successfully unblocked card **** **** **** ${last4}`);
        setActivePage(1);
        setPageStartIndex(1);
        fetchBlockedCards();
      } catch (e) {
        setErrorMsg(getErrorMessage(e));
      } finally {
        setIsModalLoading(false);
        setShowUnblockModal(false);
      }
    },
    [actionBlockedCard, apiAprilBaseUri, fetchBlockedCards],
  );

  const handleCancel = (e: ReactMouseEvent<HTMLButtonElement>): void => {
    e.preventDefault();
    setShowInfoModal(false);
    setShowUnblockModal(false);
  };

  const toggleDropdown = (e: ReactMouseEvent<HTMLDivElement>, blockedCard: any, index: number): void => {
    e.preventDefault();
    setActionBlockedCardIdIndex(blockedCard.cardBlockId + index.toString());
    setActionBlockedCard(blockedCard);
  };

  const handleClickOutside = () => {
    setActionBlockedCardIdIndex('');
  };
  useClickOutside(containerRef, handleClickOutside, ['link-btn', 'item-actions-list', 'item-actions-block']);

  useEffect(() => {
    fetchBlockedCards();
  }, [fetchBlockedCards, activePage, filterOptionValues, searchText]);

  useEffect(() => {
    if (searchTextInputValue.length >= 4) {
      setActivePage(1);
      setPageStartIndex(1);
      setFilterPopupOpen(false);
      setSearchText(searchTextInputValue);
    } else {
      setSearchText('');
    }
  }, [fetchBlockedCards, searchTextInputValue]);

  useEffect(() => {
    if (showUnblockModal) {
      setShowInfoModal(false);
    }
  }, [showUnblockModal]);

  return (
    <div className="mb-4 blocked-cards">
      <HeaderTitle title="Card Block Events" />
      {errorMsg.length > 0 && (
        <Content>
          <Message className="mt-2 mb-0" description={errorMsg} success={false} handleClose={() => setErrorMsg('')} />
        </Content>
      )}
      {successMsg.length > 0 && (
        <Content>
          <Message
            className="mt-2 mb-0"
            description={successMsg}
            success={true}
            handleClose={() => setSuccessMsg('')}
          />
        </Content>
      )}
      <Content>
        <P>
          <SearchBox
            type="text"
            placeholder="Last 4 digits of card number"
            onChange={(e) => setSearchTextInputValue(e.target.value)}
            value={searchTextInputValue}
          />
          <FilterBtn onClick={() => setFilterPopupOpen((prev) => !prev)}>
            Filters {filterCount > 0 && `(${filterCount})`}
          </FilterBtn>
          <FilterPopup>
            {filterPopupOpen && (
              <FilterComponent
                filterOptionValues={filterOptionValues}
                initFilterOptionValues={initFilterOptions}
                setFilterOptionValues={setFilterOptionValues}
                handlePageChange={setActivePage}
                handleFilterCount={setFilterCount}
                toggleFilter={() => setFilterPopupOpen(false)}
              />
            )}
          </FilterPopup>
        </P>
        <LpBox>
          {isLoading && (
            <SpinnerWrapper>
              <Spinner color="#0016D1" />
            </SpinnerWrapper>
          )}
          {!isLoading && blockedCards.length === 0 && (
            <LpDetails>
              <p className="text-center">No blocked cards found.</p>
            </LpDetails>
          )}
          {!isLoading && blockedCards.length > 0 && (
            <>
              <ItemList>
                <li className="item item-list-header">
                  <div className="item-row">
                    <div className={`item-col item-col-header`}>
                      <span>Card Status & Details</span>
                    </div>
                    <div className={`item-col item-col-header`}>
                      <span>Card Issuer</span>
                    </div>
                    <div className={`item-col item-col-header`}>
                      <span>Merchant</span>
                    </div>
                    <div className={`item-col item-col-header`}>
                      <span>Event Type</span>
                    </div>
                    <div className={`item-col item-col-header`}>
                      <span>Date and Time</span>
                    </div>
                    <div></div>
                  </div>
                </li>
                {blockedCards.map((blockedCard, index) => (
                  <li className="item active-item" key={blockedCard.cardBlockId + index.toString()}>
                    <div className="item-row item-row-reset-m">
                      <div className="item-col">
                        <div className="item text-left mt-1 pl-m slug">
                          {blockedCard.paymentMethod?.paymentMethodType === 'Card' ? (
                            <span
                              className={`card-status-icon ${
                                blockedCard.isCardBlocked ? 'status-blocked' : 'status-unblocked'
                              }`}
                              title={blockedCard.isCardBlocked ? 'Blocked' : 'Unblocked'}
                            >
                              {blockedCard.isCardBlocked ? <BlockedIcon /> : <CheckIcon />}
                              {`${blockedCard.paymentMethod?.cardBrand} [${blockedCard.paymentMethod.cardFunding}] *${blockedCard.paymentMethod.cardLast4}`}
                            </span>
                          ) : (
                            '-'
                          )}
                        </div>
                      </div>
                      <div className="item-col">
                        <span>
                          {blockedCard.paymentMethod?.paymentMethodType === 'Card'
                            ? `${blockedCard.paymentMethod?.cardIssuer}`
                            : '-'}
                        </span>
                      </div>
                      <div className="item-col">
                        <span>{blockedCard.purchaseDetails?.merchantName ?? '-'}</span>
                      </div>
                      <div className="item-col">
                        <span className={`item-label ${blockedCardEventTypeLabels[blockedCard.eventType].className}`}>
                          {blockedCardEventTypeLabels[blockedCard.eventType].label}
                        </span>
                      </div>
                      <div className="item-col">
                        <span>{DateTime.fromISO(blockedCard.createdAt).toFormat('dd/LL/yyyy, HH:mm')}</span>
                      </div>
                      <div className="item-col fixed item-col-actions-dropdown">
                        <div className="item-actions-dropdown" onClick={(e) => toggleDropdown(e, blockedCard, index)}>
                          <DotsIcon className="dots-icon" />
                        </div>
                        <div
                          className={`item-actions-block ${
                            actionBlockedCardIdIndex === blockedCard.cardBlockId + index.toString() ? 'active' : ''
                          }`}
                          ref={containerRef}
                        >
                          <ul className="item-actions-list">
                            <li>
                              <NavLink
                                className="link-btn"
                                to={`#`}
                                onClick={(e) => {
                                  e.preventDefault();
                                  setShowInfoModal(true);
                                  setActionBlockedCardIdIndex('');
                                }}
                              >
                                <RemoveRedEyeIcon /> View Details
                              </NavLink>
                            </li>
                            {blockedCard.isCardBlocked && (
                              <li>
                                <NavLink className="link-btn" to={`#`} onClick={() => setShowUnblockModal(true)}>
                                  <CloseIcon /> Unblock Card
                                </NavLink>
                              </li>
                            )}
                          </ul>
                        </div>
                      </div>
                    </div>
                  </li>
                ))}
              </ItemList>

              <PaginationRow>
                <Pagination
                  pageCount={pageCount}
                  activePage={activePage}
                  setActivePage={setActivePage}
                  pageStartIndex={pageStartIndex}
                  setPageStartIndex={setPageStartIndex}
                />
              </PaginationRow>
            </>
          )}
        </LpBox>
      </Content>
      {showInfoModal && (
        <InfoModal
          handleCancel={handleCancel}
          handleSubmit={(e) => {
            e.preventDefault();
            setShowUnblockModal(true);
          }}
          actionBlockedCard={actionBlockedCard}
        />
      )}
      {showUnblockModal && (
        <UnblockModal
          handleCancel={handleCancel}
          handleSubmit={(e) => {
            e.preventDefault();
            actionBlockedCard && unBlockCard(actionBlockedCard.cardBlockId);
          }}
          isModalLoading={isModalLoading}
          actionBlockedCard={actionBlockedCard}
        />
      )}
    </div>
  );
};

export default BlockedCards;
